// Categoriza as viagens em "atual", "próxima" (mais próxima futura),
// "futuras" (todas que ainda não terminaram, exceto a do hero) e "passadas".
//
// Critério:
//   - atual:    today >= startDate && today <= endDate
//   - próxima:  startDate > today  (ordenada pela mais próxima)
//   - passada:  endDate < today
//
// Datas no Firestore podem estar em formatos variados (legado):
//   - 'YYYY-MM-DD'         (ideal, novo)
//   - 'MM-DD' ou 'DD/MM'   (com `trip.year` separado)
//   - 'DD/MM/YYYY'         (display antigo)
//   - vazio ou null
// `normalizeDate(value, fallbackYear)` converte tudo pra 'YYYY-MM-DD'.
// Comparação é em string ISO, evitando timezone bugs.

export function categorizeTrips(trips, todayIso = today()) {
  const t = String(todayIso);
  const current = [];
  const future = [];
  const past = [];

  for (const trip of trips) {
    const start = normalizeDate(trip.startDate, trip.year);
    const end   = normalizeDate(trip.endDate,   trip.year);

    if (start && end && start <= t && end >= t) {
      current.push(trip);
    } else if (start && start > t) {
      future.push(trip);
    } else if (end && end < t) {
      past.push(trip);
    } else {
      // Sem datas ou shape estranho — vai pra futuras como segura
      future.push(trip);
    }
  }

  // Ordenação (usa datas normalizadas)
  current.sort((a, b) =>
    normalizeDate(a.endDate, a.year).localeCompare(normalizeDate(b.endDate, b.year)));
  future.sort((a, b) =>
    normalizeDate(a.startDate, a.year).localeCompare(normalizeDate(b.startDate, b.year)));
  past.sort((a, b) =>
    normalizeDate(b.endDate, b.year).localeCompare(normalizeDate(a.endDate, a.year)));

  // O hero é a primeira viagem atual; se não houver, a próxima futura mais perto.
  const hero = current[0] || future[0] || null;
  // Próximas:
  //  - Se temos viagem em curso (hero é "current"), as futuras vão TODAS na seção;
  //    a viagem em curso aparece SÓ no hero, não duplica.
  //  - Se não há viagem em curso (hero é a próxima futura), mostramos TODAS as
  //    futuras na seção, INCLUSIVE o hero — duplicação intencional pra reforçar
  //    a viagem mais próxima.
  const upcoming = current.length
    ? future.filter((t) => t.id !== hero?.id)
    : future.slice();

  return { hero, current, upcoming, past };
}

/**
 * Para o hero: descreve o status — "Em viagem · dia X de Y" se em curso,
 * ou "Faltam N dias para sua viagem" se futura. Retorna { kind, text }
 * pra a UI escolher cor.
 *
 * Quando shape `short=true`, retorna versões curtas pra usar em cards
 * pequenos (ex: "Em 108 dias", "Hoje").
 */
export function heroStatus(trip, todayIso = today(), { short = false } = {}) {
  if (!trip) return null;
  const t = String(todayIso);
  const start = normalizeDate(trip.startDate, trip.year);
  const end   = normalizeDate(trip.endDate,   trip.year);

  if (start && end && start <= t && end >= t) {
    const total = daysBetween(start, end) + 1;
    const elapsed = daysBetween(start, t) + 1;
    return { kind: 'current', text: `Em viagem · dia ${elapsed} de ${total}` };
  }

  if (start && start > t) {
    const days = daysBetween(t, start);
    if (days === 0) return { kind: 'future', text: short ? 'Hoje' : 'Sua viagem começa hoje' };
    if (days === 1) return { kind: 'future', text: short ? 'Em 1 dia' : 'Falta 1 dia para sua viagem' };
    return {
      kind: 'future',
      text: short ? `Em ${days} dias` : `Faltam ${days} dias para sua viagem`,
    };
  }

  if (end && end < t) {
    return { kind: 'past', text: 'Concluída' };
  }

  return { kind: 'future', text: short ? 'A planejar' : 'Sem datas definidas' };
}

/**
 * Aplica busca em-memória nas viagens. Procura em: name, description,
 * cidades, hotéis e rotas. Retorna nova array com matches.
 */
export function searchTrips(trips, queryRaw) {
  const q = (queryRaw || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  if (!q) return trips;
  const tokens = q.split(/\s+/).filter(Boolean);

  return trips.filter((trip) => {
    const haystack = buildHaystack(trip);
    return tokens.every((tok) => haystack.includes(tok));
  });
}

// ---------- internals ----------

function buildHaystack(trip) {
  const parts = [];
  parts.push(trip.name || '');
  parts.push(trip.description || '');
  if (Array.isArray(trip.cities)) {
    for (const c of trip.cities) {
      if (typeof c === 'string') parts.push(c);
      else if (c && typeof c === 'object') {
        parts.push(c.name || '', c.country || '', c.city || '');
      }
    }
  }
  if (Array.isArray(trip.hotels)) {
    for (const h of trip.hotels) parts.push(h?.name || '', h?.city || '');
  }
  if (Array.isArray(trip.routes)) {
    for (const r of trip.routes) parts.push(r?.from || '', r?.to || '', r?.label || '');
  }
  return parts.join(' ').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function today() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function daysBetween(isoA, isoB) {
  // Diferença em dias entre duas datas YYYY-MM-DD. Sempre positiva.
  const a = new Date(isoA + 'T00:00:00Z').getTime();
  const b = new Date(isoB + 'T00:00:00Z').getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return NaN;
  return Math.abs(Math.round((b - a) / 86400000));
}

/**
 * Normaliza uma data em string pra 'YYYY-MM-DD'. Aceita:
 *   - 'YYYY-MM-DD' ou 'YYYY-MM-DDTHH:mm:ss…' → corta no T
 *   - 'YYYY/MM/DD' → troca / por -
 *   - 'DD/MM/YYYY' (PT-BR) ou 'MM/DD/YYYY' (US) — desambiguado pelo segundo
 *     número se ele for > 12 (não pode ser mês)
 *   - 'DD/MM' ou 'MM-DD' (sem ano) → assume `fallbackYear` (default = atual);
 *     desambiguação: se primeiro > 12 ou segundo <= 12, default PT-BR (DD/MM).
 *     Apenas quando segundo > 12 e primeiro <= 12 (ex: '08/13'), interpreta
 *     como MM/DD. Default explícito = DD/MM (português brasileiro) — esse
 *     é o app-alvo do Mapiare.
 *   - Date object ou Timestamp Firestore com toDate() → ISO
 *   - vazio/null/undefined → ''
 *
 * Datas inválidas/incompreensíveis viram '' (chamadora deve tratar).
 */
export function normalizeDate(value, fallbackYear) {
  if (value == null || value === '') return '';

  // Firestore Timestamp { toDate() }
  if (typeof value === 'object' && typeof value.toDate === 'function') {
    return dateToIso(value.toDate());
  }
  if (value instanceof Date) {
    return dateToIso(value);
  }

  let s = String(value).trim();
  if (!s) return '';

  // Corta hora se tiver
  const tIdx = s.indexOf('T');
  if (tIdx >= 0) s = s.slice(0, tIdx);

  // YYYY-MM-DD ou YYYY/MM/DD (ano explícito vem primeiro)
  let m = s.match(/^(\d{4})[\-/](\d{1,2})[\-/](\d{1,2})$/);
  if (m) return `${m[1]}-${pad2(m[2])}-${pad2(m[3])}`;

  // DD/MM/YYYY (default PT-BR) ou MM/DD/YYYY (apenas quando segundo > 12)
  m = s.match(/^(\d{1,2})[\-/](\d{1,2})[\-/](\d{4})$/);
  if (m) {
    const a = Number(m[1]);
    const b = Number(m[2]);
    const [dd, mm] = (b > 12 && a <= 12) ? [b, a] : [a, b];
    return `${m[3]}-${pad2(mm)}-${pad2(dd)}`;
  }

  // DD/MM ou DD-MM (sem ano) — usa fallbackYear. Default PT-BR: DD/MM.
  m = s.match(/^(\d{1,2})[\-/](\d{1,2})$/);
  if (m) {
    const yr = String(fallbackYear || new Date().getFullYear());
    const a = Number(m[1]);
    const b = Number(m[2]);
    const [dd, mm] = (b > 12 && a <= 12) ? [b, a] : [a, b];
    return `${yr}-${pad2(mm)}-${pad2(dd)}`;
  }

  // Última tentativa: parser nativo
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return dateToIso(d);

  return '';
}

function pad2(n) { return String(n).padStart(2, '0'); }
function dateToIso(d) {
  const yyyy = d.getFullYear();
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  return `${yyyy}-${mm}-${dd}`;
}
